import PropTypes from 'prop-types';
import clsx from 'clsx';
import { ArrowDown, ArrowUp, Check, Minus, Target } from 'lucide-react';
import { STAT_CARD_BASE, STAT_CHIP } from '../ui/StatCard';
import {
  movementSense, formatDelta, kpiBarState, metricLevel, targetStatus, formatTarget,
} from '../../utils/simulationEngine';

/**
 * The plant's vital signs. Shows the cumulative value for each KPI and, when a round
 * has just resolved, the delta that round caused.
 *
 * Colour encodes SENSE (did this move the right way), never magnitude — a big move in
 * the right direction and a big move in the wrong direction must be instantly
 * distinguishable across a projector at the back of a training room.
 */
/**
 * The bar measures CHANGE against the cap. With no starting value change and level are
 * the same number; with one, "below zero" would contradict the level shown above it.
 */
const offScaleNote = (kpi, overCap) => {
  const unit = kpi.unit ? ` ${kpi.unit}` : '';
  if (Number(kpi.start)) {
    return overCap ? `off the scale — moved more than ${kpi.cap}${unit} from the start` : 'off the scale — below where it started';
  }
  return overCap ? `off the scale — past ${kpi.cap}${unit}` : 'off the scale — below zero';
};

const KpiStrip = ({ kpis, values, deltas, showBar = true, compact = false, showTargetStatus = true }) => (
  <div
    className={clsx(
      'grid gap-3',
      compact ? 'grid-cols-2 sm:grid-cols-5' : 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-5'
    )}
  >
    {kpis.map((kpi) => {
      // `values` hold CHANGE since the start; the number shown is the level.
      const change = values?.[kpi.key] ?? 0;
      const value = metricLevel(kpi, change);
      const delta = deltas?.[kpi.key];
      const sense = movementSense(kpi, delta);
      // pct alone cannot say whether the bar is a measurement or a clamped stub.
      const { pct, overCap, belowFloor } = kpiBarState(kpi, change);
      const target = formatTarget(kpi);
      const status = showTargetStatus ? targetStatus(kpi, change) : null;

      const DeltaIcon = !delta ? Minus : delta > 0 ? ArrowUp : ArrowDown;

      return (
        <div key={kpi.key} className={clsx(STAT_CARD_BASE, compact ? 'p-3' : 'p-4')}>
          <div className="flex items-start justify-between gap-2">
            <span className="text-[11px] font-medium uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
              {kpi.label}
            </span>
            <span
              className={clsx(
                'shrink-0 rounded-md px-1.5 py-0.5 text-[10px] font-semibold',
                STAT_CHIP[kpi.variant] || STAT_CHIP.slate
              )}
            >
              {kpi.direction === 'lower' ? '↓' : kpi.direction === 'higher' ? '↑' : '↔'}
            </span>
          </div>

          <div className="mt-1.5 flex items-baseline gap-2">
            <span className="text-xl font-semibold tabular-nums text-neutral-900 dark:text-neutral-100">
              {value}
            </span>
            {deltas && (
              <span
                className={clsx(
                  'inline-flex items-center gap-0.5 text-xs font-semibold tabular-nums',
                  sense === 'good' && 'text-emerald-600 dark:text-emerald-400',
                  sense === 'bad' && 'text-red-600 dark:text-red-400',
                  sense === 'neutral' && 'text-neutral-400 dark:text-neutral-500'
                )}
              >
                <DeltaIcon size={12} strokeWidth={2.5} />
                {formatDelta(delta)}
              </span>
            )}
          </div>

          <p className="mt-0.5 text-[11px] text-neutral-400 dark:text-neutral-500">{kpi.unit}</p>

          {target && (
            <p
              className={clsx(
                'mt-1 inline-flex items-center gap-1 text-[11px] font-medium',
                status === 'met' && 'text-emerald-600 dark:text-emerald-400',
                status === 'missed' && 'text-amber-600 dark:text-amber-400',
                !status && 'text-neutral-500 dark:text-neutral-400'
              )}
            >
              {status === 'met' ? <Check size={11} strokeWidth={3} /> : <Target size={11} strokeWidth={2} />}
              Target {target}
            </p>
          )}

          {showBar && (
            <>
              <div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-neutral-100 dark:bg-neutral-800">
                <div
                  className={clsx(
                    'h-full rounded-full transition-all duration-500',
                    // A value off the BOTTOM of the scale draws a 0%-width bar. Left on the
                    // magnitude ramp below that reads as emerald — the "all fine" colour for
                    // the worst result on the board. Force red so the colour cannot contradict
                    // the number sitting above it.
                    belowFloor ? 'bg-red-500'
                      : pct >= 80 ? 'bg-red-500'
                      : pct >= 50 ? 'bg-amber-500'
                      : 'bg-emerald-500'
                  )}
                  style={{ width: `${belowFloor ? 100 : pct}%` }}
                />
              </div>
              {/* The bar is clamped here, so say so rather than let a saturated stub pass
                  for a measurement. Past the cap every value looks identical; below zero
                  every value looks like nothing happened. */}
              {(overCap || belowFloor) && (
                <p className="mt-1 text-[10px] font-medium text-red-500 dark:text-red-400">
                  {offScaleNote(kpi, overCap)}
                </p>
              )}
            </>
          )}
        </div>
      );
    })}
  </div>
);

KpiStrip.propTypes = {
  kpis: PropTypes.arrayOf(PropTypes.object).isRequired,
  values: PropTypes.object,
  deltas: PropTypes.object,
  showBar: PropTypes.bool,
  compact: PropTypes.bool,
  /** Off where nothing has been played yet (the builder), so "missed" is not implied. */
  showTargetStatus: PropTypes.bool,
};

export default KpiStrip;
